import { blogArticles, getArticleUrl, BLOG_BASE_PATH } from '../blog';

export interface RouteSEO {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  jsonLd?: object;
  h1?: string;
}

const BASE_URL = 'https://diagnosti-k.fr';
const DEFAULT_IMAGE = `${BASE_URL}/image.png`;

export const seoRoutes: Record<string, RouteSEO> = {
  '/': {
    title: 'DIAGNOSTI-K | Diagnostics Immobiliers Camaret-sur-Aigues – DPE, Amiante, Electricité',
    description: 'Diagnostics immobiliers certifiés à Camaret-sur-Aigues et région Vaucluse/Drôme. DPE, amiante, électricité, gaz, plomb, Carrez, Boutin. Rapport sous 48h. Devis gratuit.',
    canonical: `${BASE_URL}/`,
    ogImage: DEFAULT_IMAGE,
    h1: 'Diagnostics Immobiliers Certifiés – Camaret-sur-Aigues',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'DIAGNOSTI-K SARL',
      description: 'Expert en diagnostics immobiliers certifiés à Camaret-sur-Aigues. DPE, amiante, électricité, gaz, plomb, Carrez, Boutin.',
      url: BASE_URL,
      telephone: '+33789307500',
      email: 'Spskdiag@gmail.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '95 avenue Fernand Gonnet',
        addressLocality: 'Camaret-sur-Aigues',
        postalCode: '84850',
        addressCountry: 'FR'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '44.1167',
        longitude: '4.8500'
      },
      openingHours: ['Mo-Fr 08:00-19:00', 'Sa 00:00-00:00'],
      priceRange: '€€',
      image: DEFAULT_IMAGE,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '28',
        bestRating: '5'
      }
    }
  },
  '/services': {
    title: 'Services Diagnostics Immobiliers | DPE, Amiante, Electricité, Gaz – DIAGNOSTI-K',
    description: 'Tous nos diagnostics immobiliers obligatoires : DPE, amiante, électricité, gaz, plomb, Loi Carrez, Loi Boutin, termites, ERP. Vente et location. Certifié et assuré.',
    canonical: `${BASE_URL}/services`,
    ogImage: DEFAULT_IMAGE,
    h1: 'Nos Services de Diagnostics Immobiliers – Vente et Location',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      provider: { '@type': 'LocalBusiness', name: 'DIAGNOSTI-K SARL' },
      serviceType: 'Diagnostic Immobilier',
      areaServed: 'Vaucluse, Drôme, Gard, Bouches-du-Rhône',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Diagnostics Immobiliers',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'DPE – Diagnostic de Performance Énergétique' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Diagnostic Amiante' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Diagnostic Électricité' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Diagnostic Gaz' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Diagnostic Plomb (CREP)' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Loi Carrez – Mesurage Vente Copropriété' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Loi Boutin – Mesurage Location' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Diagnostic Termites' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'État des Risques et Pollutions (ERP)' } }
        ]
      }
    }
  },
  '/audit-energetique': {
    title: 'Audit Énergétique Obligatoire – Classes E, F, G | DIAGNOSTI-K',
    description: 'Audit énergétique certifié obligatoire pour la vente des logements classés E, F ou G. Scénarios de rénovation, estimations des coûts et aides. Vaucluse et Drôme.',
    canonical: `${BASE_URL}/audit-energetique`,
    ogImage: DEFAULT_IMAGE,
    h1: 'Audit Énergétique Certifié – Logements Classés E, F ou G',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      provider: { '@type': 'LocalBusiness', name: 'DIAGNOSTI-K SARL' },
      serviceType: 'Audit Énergétique',
      description: 'Audit énergétique réglementaire pour logements classés E, F ou G mis en vente. Obligatoire depuis 2023.',
      areaServed: 'Vaucluse, Drôme, Gard, Bouches-du-Rhône'
    }
  },
  '/realisations': {
    title: 'Réalisations & Projets | Diagnostics Immobiliers – DIAGNOSTI-K',
    description: 'Découvrez nos réalisations de diagnostics immobiliers dans le Vaucluse et la Drôme. Particuliers, agences immobilières et professionnels.',
    canonical: `${BASE_URL}/realisations`,
    ogImage: DEFAULT_IMAGE,
    h1: 'Nos Réalisations de Diagnostics Immobiliers'
  },
  '/contact': {
    title: 'Contact & Devis Gratuit | Diagnostics Immobiliers – DIAGNOSTI-K',
    description: 'Contactez DIAGNOSTI-K pour un devis gratuit. Diagnostics immobiliers à Camaret-sur-Aigues, Vaucluse et Drôme. Réponse rapide, rapport sous 48h.',
    canonical: `${BASE_URL}/contact`,
    ogImage: DEFAULT_IMAGE,
    h1: 'Contactez-nous – Devis Gratuit en 24h',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      url: `${BASE_URL}/contact`,
      name: 'Contact DIAGNOSTI-K',
      description: 'Formulaire de contact et demande de devis pour diagnostics immobiliers.'
    }
  },
  '/diagnostic-valence-drome': {
    title: 'Diagnostic Immobilier Valence Drôme | DPE, Amiante, Électricité – DIAGNOSTI-K',
    description: 'Diagnostics immobiliers certifiés à Valence et dans la Drôme. DPE, amiante, électricité, gaz, plomb. Déplacement rapide, rapport sous 48h. Devis gratuit.',
    canonical: `${BASE_URL}/diagnostic-valence-drome`,
    ogImage: DEFAULT_IMAGE,
    h1: 'Diagnostic Immobilier à Valence et dans la Drôme'
  },
  '/diagnostic-ardeche-saint-peray': {
    title: 'Diagnostic Immobilier Ardèche – Saint-Péray | DPE, Amiante – DIAGNOSTI-K',
    description: 'Diagnostics immobiliers en Ardèche et à Saint-Péray. DPE, amiante, électricité, gaz, plomb. Professionnel certifié, rapport rapide. Devis gratuit.',
    canonical: `${BASE_URL}/diagnostic-ardeche-saint-peray`,
    ogImage: DEFAULT_IMAGE,
    h1: 'Diagnostic Immobilier en Ardèche – Saint-Péray et environs'
  },
  '/diagnostic-orange-vaucluse': {
    title: 'Diagnostic Immobilier Orange Vaucluse | DPE, Électricité, Gaz – DIAGNOSTI-K',
    description: 'Expert en diagnostics immobiliers à Orange et dans le Vaucluse. DPE, amiante, électricité, gaz, plomb, Carrez, Boutin. Certifié, rapport sous 48h.',
    canonical: `${BASE_URL}/diagnostic-orange-vaucluse`,
    ogImage: DEFAULT_IMAGE,
    h1: 'Diagnostic Immobilier à Orange et dans le Vaucluse'
  },
  '/diagnostic-avignon-region': {
    title: 'Diagnostic Immobilier Avignon et Région | DPE, Amiante – DIAGNOSTI-K',
    description: 'Diagnostics immobiliers certifiés à Avignon et dans sa région (Vaucluse, Gard). DPE, amiante, électricité, gaz, plomb. Devis gratuit, rapport rapide.',
    canonical: `${BASE_URL}/diagnostic-avignon-region`,
    ogImage: DEFAULT_IMAGE,
    h1: 'Diagnostic Immobilier à Avignon et sa Région'
  },
  '/mentions-legales': {
    title: 'Mentions Légales | DIAGNOSTI-K',
    description: 'Mentions légales de DIAGNOSTI-K SARL – diagnostics immobiliers à Camaret-sur-Aigues.',
    canonical: `${BASE_URL}/mentions-legales`,
    h1: 'Mentions Légales'
  },
  '/politique-confidentialite': {
    title: 'Politique de Confidentialité | DIAGNOSTI-K',
    description: 'Politique de confidentialité et protection des données personnelles de DIAGNOSTI-K SARL.',
    canonical: `${BASE_URL}/politique-confidentialite`,
    h1: 'Politique de Confidentialité'
  },
  '/conditions-generales': {
    title: 'Conditions Générales | DIAGNOSTI-K',
    description: 'Conditions générales de vente et de service de DIAGNOSTI-K SARL.',
    canonical: `${BASE_URL}/conditions-generales`,
    h1: 'Conditions Générales'
  }
};

export function getSEO(pathname: string): RouteSEO {
  if (pathname === BLOG_BASE_PATH) {
    return {
      title: 'Blog Diagnostic Immobilier | DPE, Amiante, Électricité – DIAGNOSTI-K',
      description:
        'Guides et conseils sur les diagnostics immobiliers obligatoires : DPE, amiante, électricité, gaz, plomb, Carrez, Boutin. Camaret-sur-Aigues, Orange, Vaucluse.',
      canonical: `${BASE_URL}/blog`,
      h1: 'Blog Diagnostic Immobilier',
    };
  }

  const articleMatch = pathname.startsWith(`${BLOG_BASE_PATH}/`)
    ? blogArticles.find((a) => pathname === getArticleUrl(a.slug))
    : undefined;

  if (articleMatch) {
    return {
      title: `${articleMatch.title} | DIAGNOSTI-K`,
      description: `${articleMatch.keyword} : guide complet par un diagnostiqueur certifié à Camaret-sur-Aigues et dans le Vaucluse.`,
      canonical: `${BASE_URL}${getArticleUrl(articleMatch.slug)}`,
      h1: articleMatch.keyword,
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: articleMatch.title,
        description: `${articleMatch.keyword} : guide complet par un diagnostiqueur certifié.`,
        url: `${BASE_URL}${getArticleUrl(articleMatch.slug)}`,
        datePublished: articleMatch.date,
        dateModified: articleMatch.date,
        author: { '@type': 'Organization', name: 'DIAGNOSTI-K SARL' },
        publisher: {
          '@type': 'Organization',
          name: 'DIAGNOSTI-K SARL',
          logo: { '@type': 'ImageObject', url: `${BASE_URL}/logo.png` },
        },
        image: DEFAULT_IMAGE,
        isPartOf: { '@type': 'WebSite', url: BASE_URL, name: 'DIAGNOSTI-K' },
      },
    };
  }

  return seoRoutes[pathname] ?? {
    title: 'DIAGNOSTI-K | Diagnostics Immobiliers Certifiés',
    description: 'Expert en diagnostics immobiliers à Camaret-sur-Aigues. DPE, amiante, électricité, gaz, plomb. Certifié et assuré.',
    canonical: `${BASE_URL}${pathname}`
  };
}
